import { refreshAccessToken, FORCE_LOGOUT_EVENT } from "./sessionRefresh";

// Re-exported for existing importers.
export { FORCE_LOGOUT_EVENT };

const isAuthPath = (url) =>
  typeof url === "string" &&
  (url.includes("/auth/refresh") || url.includes("/auth/login"));

/**
 * Attach the standard request + 401-refresh-and-retry response interceptors.
 *
 * The refresh itself (single-flight coalescing, transient-retry, and the
 * decision of when the session is truly over) lives in `sessionRefresh.js` so
 * that every axios client AND the proactive scheduler share one implementation.
 *
 * @param {import("axios").AxiosInstance} instance axios client to wire up.
 */
export function attachAuthInterceptor(instance) {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);

      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // A 401 from login/refresh is handled inside sessionRefresh (a fatal
      // refresh dispatches forceLogout); just surface it, don't retry.
      if (isAuthPath(originalRequest.url)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const token = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return instance(originalRequest);
      } catch (err) {
        // sessionRefresh already fired forceLogout iff this was fatal (refresh
        // token rejected). On a transient failure we keep the session and just
        // let this request fail — the caller/user can retry.
        return Promise.reject(err);
      }
    }
  );
}
